import React, { useEffect, useMemo, useState } from 'react';
import Table from '../../components/Table/Table';
import { Pagination } from '../../components/Common';
import { carDocumentsService } from '../../services';
import { useCars } from '../../hooks/useCars';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { API_CONFIG } from '../../config/api.config';
import { Car } from '../../types';
import { resolveDocumentExpiry } from './documentHelpers';

interface CarDocumentRow {
  id: number;
  documentId: string;
  documentType: string;
  documentNumber?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  filePath?: string | null;
  status?: string;
  car?: any;
  carId?: string;
}

const fileUrl = (path: string): string =>
  path.startsWith('http') ? path : `${API_CONFIG.BASE_URL}${path}`;

const documentStatus = (doc: CarDocumentRow, car?: Car): 'valid' | 'expiring_soon' | 'expired' => {
  const expiryDate = resolveDocumentExpiry(doc.documentType, doc, car);
  if (expiryDate) {
    const days = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'expired';
    if (days <= 30) return 'expiring_soon';
  }
  const stored = doc.status || (doc as any).car_document_status;
  return stored === 'expired' || stored === 'expiring_soon' ? stored : 'valid';
};

const STATUS_STYLES: Record<string, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring_soon: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<string, string> = {
  valid: 'Valid',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
};

const CarDocumentsSection: React.FC = () => {
  const carsQuery = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'code', order: 'asc' as const }],
  }), []);
  const { data: cars = [] } = useCars(carsQuery);

  const [documents, setDocuments] = useState<CarDocumentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebouncedValue(searchTerm);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  // Search runs server-side (including the linked car) so it covers the whole dataset
  useEffect(() => {
    let active = true;
    setIsLoading(true);
    carDocumentsService
      .getAll({
        pagination: { page, pageSize },
        sort: [{ field: 'createdAt', order: 'desc' as const }],
        ...(debouncedSearch ? { filters: { $or: [
          { documentType: { $containsi: debouncedSearch } },
          { documentNumber: { $containsi: debouncedSearch } },
          { car: { code: { $containsi: debouncedSearch } } },
          { car: { plateNumber: { $containsi: debouncedSearch } } },
          { car: { ownerName: { $containsi: debouncedSearch } } },
        ] } } : {}),
      })
      .then((response: any) => {
        if (!active) return;
        setDocuments(response.data || []);
        setTotal(response.meta?.pagination?.total ?? (response.data || []).length);
      })
      .catch(() => undefined)
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [version, page, pageSize, debouncedSearch]);

  const findCar = (doc: CarDocumentRow): Car | undefined => {
    const carRef = doc.car;
    const refId = typeof carRef === 'object' && carRef
      ? String(carRef.documentId || carRef.id || '')
      : String(carRef || doc.carId || '');
    if (!refId) return undefined;
    return cars.find(c => String(c.documentId) === refId || String(c.id) === refId);
  };

  const handleDelete = async (doc: CarDocumentRow) => {
    if (!window.confirm(`Are you sure you want to delete document ${doc.documentNumber || doc.documentType}?`)) return;
    await carDocumentsService.delete(doc.documentId);
    setVersion(v => v + 1);
  };

  const handleEdit = () => {
    window.alert('هذا مستند مرفوع من ملف السيارة — يمكن تعديله من صفحة Cars بزر تعديل السيارة');
  };

  const columns = [
    { key: 'carCode', label: 'Car Code', sortable: true, render: (_: any, item: CarDocumentRow) => findCar(item)?.code || '-' },
    { key: 'ownerName', label: 'Owner Name', sortable: true, render: (_: any, item: CarDocumentRow) => findCar(item)?.ownerName || '-' },
    { key: 'plateNumber', label: 'Plate Number', sortable: true, render: (_: any, item: CarDocumentRow) => findCar(item)?.plateNumber || '-' },
    { key: 'documentType', label: 'Document Type', sortable: true },
    { key: 'documentNumber', label: 'Document Number', sortable: true, render: (value: string) => value || '-' },
    { key: 'expiryDate', label: 'Expiry Date', sortable: true, render: (_: string, item: CarDocumentRow) => {
      const expiry = resolveDocumentExpiry(item.documentType, item, findCar(item));
      return expiry ? new Date(expiry).toLocaleDateString() : '-';
    } },
    { key: 'status', label: 'Status', render: (_: any, item: CarDocumentRow) => {
      const status = documentStatus(item, findCar(item));
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>
          {STATUS_LABELS[status]}
        </span>
      );
    } },
    { key: 'filePath', label: 'File', render: (value: string) => value ? (
      <a href={fileUrl(value)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
        View File
      </a>
    ) : <span>-</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Car Documents</h2>
          <p className="text-sm text-gray-500">Licenses and papers uploaded on cars — add or edit them from the Cars page</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search car documents..."
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
            {total} documents
          </span>
        </div>
      </div>

      <div className="card">
        {isLoading && documents.length === 0 ? (
          <div className="flex justify-center items-center h-40 text-gray-500">Loading car documents...</div>
        ) : (
          <>
            <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
              <Table
                columns={columns}
                data={documents}
                onEdit={handleEdit}
                onDelete={handleDelete}
                showAddButton={false}
                searchable={false}
              />
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CarDocumentsSection;
